/**
 * Mail Quick-Actions Admin - Konfiguration der 1-Klick Entscheidungen
 * v2.9.6
 */
import { useState, useEffect, useCallback } from 'react';
import {
  Zap, Plus, Edit2, Save, X, Trash2, Check,
  FileText, Users, Tag, Mail, Forward, AlertCircle, Eye, EyeOff, RefreshCw
} from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const COLOR_OPTIONS = [
  { value: 'emerald', label: 'Gruen', cls: 'bg-emerald-100 text-emerald-700 border-emerald-300' },
  { value: 'red', label: 'Rot', cls: 'bg-red-100 text-red-700 border-red-300' },
  { value: 'purple', label: 'Lila', cls: 'bg-purple-100 text-purple-700 border-purple-300' },
  { value: 'blue', label: 'Blau', cls: 'bg-blue-100 text-blue-700 border-blue-300' },
  { value: 'amber', label: 'Gelb', cls: 'bg-amber-100 text-amber-700 border-amber-300' },
  { value: 'gray', label: 'Grau', cls: 'bg-gray-100 text-gray-700 border-gray-300' },
];

const ICON_OPTIONS = [
  { value: 'FileText', label: 'Dokument', C: FileText },
  { value: 'Trash2', label: 'Papierkorb', C: Trash2 },
  { value: 'Zap', label: 'Blitz', C: Zap },
  { value: 'Users', label: 'Personen', C: Users },
  { value: 'Tag', label: 'Tag', C: Tag },
  { value: 'Mail', label: 'Brief', C: Mail },
  { value: 'Forward', label: 'Weiterleiten', C: Forward },
  { value: 'AlertCircle', label: 'Warnung', C: AlertCircle },
];

const ARCHIVE_MODES = [
  { value: 'always', label: 'Immer archivieren' },
  { value: 'never', label: 'Nie archivieren' },
  { value: 'follow_toggle', label: 'Toggle beachten' },
];

const AUTOMATION_MODES = [
  { value: 'manual', label: 'Manuell' },
  { value: 'suggestion', label: 'Vorschlag' },
  { value: 'automatic', label: 'Automatisch' },
];

const EMPTY = {
  slug: '', name: '', button_label: '', color_variant: 'gray', icon: 'Tag',
  category: '', email_visibility: 'admin_only', forward_to: '',
  archive_mode: 'follow_toggle', sort_order: 100, automation_mode: 'suggestion',
};

const Field = ({ l, v, fn, ph, type, tid }) => (
  <div>
    <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">{l}</label>
    <input type={type||'text'} value={v} onChange={(e) => fn(e.target.value)} placeholder={ph}
      className="w-full px-2.5 py-1.5 text-sm rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
      data-testid={tid} />
  </div>
);

const Sel = ({ l, v, fn, opts, tid }) => (
  <div>
    <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">{l}</label>
    <select value={v} onChange={(e) => fn(e.target.value)}
      className="w-full px-2.5 py-1.5 text-sm rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
      data-testid={tid}>
      {opts.map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
    </select>
  </div>
);

const slugify = (v) => {
  const map = {'ä':'ae','ö':'oe','ü':'ue','ß':'ss','Ä':'ae','Ö':'oe','Ü':'ue'};
  let s = v.toLowerCase();
  for (const [from, to] of Object.entries(map)) {
    s = s.split(from).join(to);
  }
  return s.replace(/[^a-z0-9_]/g, '_').replace(/_+/g, '_').replace(/^_/, '');
};

const FormGrid = ({ f, sf, isNew }) => (
  <div className="grid grid-cols-2 gap-3">
    {isNew && <Field l="Slug (intern)" v={f.slug} fn={(v) => sf(prev => ({...prev, slug: slugify(v)}))} tid="qa-slug" />}
    <Field l="Name" v={f.name} fn={(v) => sf(prev => ({...prev, name: v}))} tid="qa-name" />
    <Field l="Button-Text" v={f.button_label} fn={(v) => sf(prev => ({...prev, button_label: v}))} tid="qa-label" />
    <Field l="Kategorie" v={f.category} fn={(v) => sf(prev => ({...prev, category: v}))} tid="qa-category" />
    <Sel l="Farbe" v={f.color_variant} fn={(v) => sf(prev => ({...prev, color_variant: v}))} opts={COLOR_OPTIONS.map(c=>({v:c.value,l:c.label}))} tid="qa-color" />
    <Sel l="Icon" v={f.icon} fn={(v) => sf(prev => ({...prev, icon: v}))} opts={ICON_OPTIONS.map(i=>({v:i.value,l:i.label}))} tid="qa-icon" />
    <Sel l="Sichtbarkeit" v={f.email_visibility} fn={(v) => sf(prev => ({...prev, email_visibility: v}))} opts={[{v:'admin_only',l:'Nur Admin'},{v:'admin_employee',l:'Admin + Mitarbeiter'}]} tid="qa-vis" />
    <Field l="Weiterleiten an" v={f.forward_to} fn={(v) => sf(prev => ({...prev, forward_to: v}))} ph="leer = keine" tid="qa-fwd" />
    <Sel l="Archivierung" v={f.archive_mode} fn={(v) => sf(prev => ({...prev, archive_mode: v}))} opts={ARCHIVE_MODES.map(m=>({v:m.value,l:m.label}))} tid="qa-archive" />
    <Sel l="Automationsmodus" v={f.automation_mode || 'suggestion'} fn={(v) => sf(prev => ({...prev, automation_mode: v}))} opts={AUTOMATION_MODES.map(m=>({v:m.value,l:m.label}))} tid="qa-automation" />
    <Field l="Reihenfolge" v={f.sort_order} fn={(v) => sf(prev => ({...prev, sort_order: parseInt(v)||0}))} type="number" tid="qa-sort" />
    {(f.automation_mode === 'automatic') && (
      <div className="col-span-2 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded p-2 text-xs text-amber-700 dark:text-amber-300">
        Nur fuer sichere Standardfaelle verwenden. Automatik wird nur bei hoher Confidence und ohne sensible Inhalte ausgefuehrt.
      </div>
    )}
  </div>
);

export const MailQuickActionsAdmin = () => {
  const [actions, setActions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [showCreate, setShowCreate] = useState(false);
  const [createForm, setCreateForm] = useState({ ...EMPTY });
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);

  const tok = () => {
    try { return JSON.parse(localStorage.getItem('gb-sylt-user')).token; } catch { return null; }
  };

  const flash = (type, text) => { setMsg({ type, text }); setTimeout(() => setMsg(null), 4000); };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/mail/quick-actions`, { headers: { Authorization: `Bearer ${tok()}` } });
      setActions(r.data.quick_actions || []);
    } catch (e) { console.error(e); } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleCreate = async () => {
    if (!createForm.slug || !createForm.name || !createForm.category) { flash('error', 'Slug, Name und Kategorie sind Pflicht'); return; }
    setSaving(true);
    try {
      const fd = new FormData();
      Object.entries(createForm).forEach(([k, v]) => { if (v !== null && v !== undefined && v !== '') fd.append(k, v.toString()); });
      await axios.post(`${API}/mail/quick-actions`, fd, { headers: { Authorization: `Bearer ${tok()}` } });
      flash('success', `"${createForm.name}" erstellt`);
      setShowCreate(false); setCreateForm({ ...EMPTY }); await load();
    } catch (e) { flash('error', e.response?.data?.detail || 'Fehler'); } finally { setSaving(false); }
  };

  const handleUpdate = async (id) => {
    setSaving(true);
    try {
      const fd = new FormData();
      Object.entries(editForm).forEach(([k, v]) => { if (v !== null && v !== undefined) fd.append(k, v.toString()); });
      await axios.put(`${API}/mail/quick-actions/${id}`, fd, { headers: { Authorization: `Bearer ${tok()}` } });
      flash('success', 'Aktualisiert'); setEditingId(null); await load();
    } catch (e) { flash('error', e.response?.data?.detail || 'Fehler'); } finally { setSaving(false); }
  };

  const handleToggle = async (a) => {
    try {
      const fd = new FormData(); fd.append('is_active', (!a.is_active).toString());
      await axios.put(`${API}/mail/quick-actions/${a.id}`, fd, { headers: { Authorization: `Bearer ${tok()}` } });
      flash('success', a.is_active ? 'Deaktiviert' : 'Aktiviert'); await load();
    } catch { flash('error', 'Fehler'); }
  };

  const startEdit = (a) => {
    setEditingId(a.id);
    setEditForm({ name: a.name, button_label: a.button_label, color_variant: a.color_variant, icon: a.icon,
      category: a.category, email_visibility: a.email_visibility, forward_to: a.forward_to || '',
      archive_mode: a.archive_mode, sort_order: a.sort_order, automation_mode: a.automation_mode || 'suggestion' });
  };

  if (loading) return <div className="flex items-center justify-center p-12"><RefreshCw className="h-6 w-6 animate-spin text-gray-400" /></div>;

  return (
    <div className="space-y-4" data-testid="quick-actions-admin">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
            <Zap className="h-5 w-5 text-amber-500" /> Quick-Actions verwalten
          </h2>
          <p className="text-sm text-gray-500 mt-0.5">1-Klick Entscheidungen fuer die Mail-Inbox</p>
        </div>
        <button onClick={() => { setShowCreate(true); setCreateForm({...EMPTY}); }}
          className="flex items-center gap-1.5 px-3 py-2 bg-amber-600 text-white rounded-lg text-sm font-medium hover:bg-amber-700"
          data-testid="qa-create-btn">
          <Plus className="h-4 w-4" /> Neue Quick-Action
        </button>
      </div>

      {msg && <div className={`p-3 rounded-lg text-sm ${msg.type==='success' ? 'bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300' : 'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300'}`}>{msg.text}</div>}

      {showCreate && (
        <div className="bg-amber-50 dark:bg-amber-900/10 border border-amber-200 dark:border-amber-800 rounded-xl p-4">
          <h3 className="font-medium text-amber-800 dark:text-amber-200 mb-3">Neue Quick-Action</h3>
          <FormGrid f={createForm} sf={setCreateForm} isNew />
          <div className="flex gap-2 mt-4">
            <button onClick={handleCreate} disabled={saving} data-testid="qa-create-save"
              className="flex items-center gap-1.5 px-3 py-1.5 bg-amber-600 text-white rounded text-sm font-medium hover:bg-amber-700 disabled:opacity-50">
              {saving ? <RefreshCw className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />} Erstellen
            </button>
            <button onClick={() => setShowCreate(false)} className="px-3 py-1.5 text-gray-600 dark:text-gray-400 text-sm hover:bg-gray-100 dark:hover:bg-gray-700 rounded">Abbrechen</button>
          </div>
        </div>
      )}

      <div className="space-y-2">
        {actions.map((a) => {
          const col = COLOR_OPTIONS.find(c => c.value === a.color_variant);
          const ico = ICON_OPTIONS.find(i => i.value === a.icon);
          const IC = ico?.C || Tag;
          const editing = editingId === a.id;
          return (
            <div key={a.id} data-testid={`qa-item-${a.slug}`}
              className={`border rounded-xl p-4 transition-all ${a.is_active ? 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800' : 'border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-gray-900 opacity-60'}`}>
              {editing ? (
                <div>
                  <span className="text-xs font-mono text-gray-400 mb-2 block">{a.slug}</span>
                  <FormGrid f={editForm} sf={setEditForm} />
                  <div className="flex gap-2 mt-3">
                    <button onClick={() => handleUpdate(a.id)} disabled={saving} data-testid={`qa-save-${a.slug}`}
                      className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 text-white rounded text-sm font-medium hover:bg-blue-700 disabled:opacity-50">
                      {saving ? <RefreshCw className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />} Speichern
                    </button>
                    <button onClick={() => setEditingId(null)} className="px-3 py-1.5 text-gray-600 dark:text-gray-400 text-sm hover:bg-gray-100 dark:hover:bg-gray-700 rounded">Abbrechen</button>
                  </div>
                </div>
              ) : (
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`p-2 rounded-lg border ${col?.cls || 'bg-gray-100 text-gray-700 border-gray-300'}`}><IC className="h-4 w-4" /></div>
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-gray-900 dark:text-white">{a.button_label}</span>
                        <span className="text-xs font-mono text-gray-400 bg-gray-100 dark:bg-gray-700 px-1.5 py-0.5 rounded">{a.slug}</span>
                        {!a.is_active && <span className="text-xs bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 px-1.5 py-0.5 rounded">inaktiv</span>}
                      </div>
                      <div className="flex items-center gap-3 text-xs text-gray-500 dark:text-gray-400 mt-1">
                        <span>Kat: {a.category}</span>
                        {a.forward_to && <span>Fwd: {a.forward_to}</span>}
                        <span>Archiv: {ARCHIVE_MODES.find(m=>m.value===a.archive_mode)?.label||a.archive_mode}</span>
                        <span className={`px-1.5 py-0.5 rounded font-medium ${
                          a.automation_mode === 'automatic' ? 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300' :
                          a.automation_mode === 'manual' ? 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400' :
                          'bg-cyan-100 dark:bg-cyan-900/30 text-cyan-700 dark:text-cyan-300'
                        }`} data-testid={`qa-mode-${a.slug}`}>
                          {AUTOMATION_MODES.find(m=>m.value===a.automation_mode)?.label || 'Vorschlag'}
                        </span>
                        <span>#{a.sort_order}</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    <button onClick={() => handleToggle(a)} data-testid={`qa-toggle-${a.slug}`}
                      className={`p-1.5 rounded transition-colors ${a.is_active ? 'text-green-600 hover:bg-green-50 dark:hover:bg-green-900/20' : 'text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'}`}
                      title={a.is_active ? 'Deaktivieren' : 'Aktivieren'}>
                      {a.is_active ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
                    </button>
                    <button onClick={() => startEdit(a)} data-testid={`qa-edit-${a.slug}`}
                      className="p-1.5 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded transition-colors"><Edit2 className="h-4 w-4" /></button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {actions.length === 0 && <div className="text-center py-8 text-gray-500 dark:text-gray-400">Keine Quick-Actions vorhanden.</div>}
    </div>
  );
};
